import { motion } from "framer-motion";
import {
  Check,
  Sparkles,
  ArrowRight,
  Building2,
  Megaphone,
  ShoppingBag,
  Boxes,
} from "lucide-react";

const TIERS = [
  {
    key: "b2b",
    name: "B2B Engagement",
    icon: Building2,
    blurb:
      "Fractional CMO + senior pod for bootstrapped, Angel and Series-A start-ups & SMEs.",
    features: [
      "Fractional marketing leadership",
      "ABM, LinkedIn Ads & Demand Gen",
      "GEO + Performance Marketing",
      "CRM, workflows & automation",
      "Lead generation & funnel ops",
      "Market research & new-market access",
    ],
    cta: "Talk to us",
    highlight: false,
  },
  {
    key: "b2c",
    name: "B2C Engagement",
    icon: Megaphone,
    blurb:
      "Performance + conversion-focused creatives for consumer brands ready to scale.",
    features: [
      "Performance marketing across Meta & Google",
      "Conversion-focused ad creatives",
      "Paid Social & Paid Search",
      "Creator & influencer marketing",
      "Retention & lifecycle",
      "Landing-page CRO",
    ],
    cta: "Most picked",
    highlight: true,
  },
  {
    key: "d2c",
    name: "D2C Engagement",
    icon: ShoppingBag,
    blurb:
      "Full-funnel growth squad for direct-to-consumer brands, launch to scale.",
    features: [
      "CRO sprints",
      "WhatsApp & email lifecycle",
      "Creative engine & branding",
      "Retention & repeat-buyer loops",
      "Performance + brand campaigns",
    ],
    cta: "Talk to us",
    highlight: false,
  },
  {
    key: "ecommerce",
    name: "Ecommerce Platform",
    icon: Boxes,
    blurb:
      "Design, build and operate storefronts on Shopify and Amazon SmartBiz.",
    features: [
      "Shopify theme & headless builds",
      "Amazon SmartBiz storefront launch",
      "A+ listings & catalog ops",
      "Ads + sponsored campaigns",
      "Tracking, GA4 & dashboards",
    ],
    cta: "Talk to us",
    highlight: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-yellow-400 font-mono-ui text-xs tracking-[0.22em] uppercase mb-4">
              · Engagement models
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter max-w-2xl">
              Pick the engagement.
              <br />
              <span className="text-zinc-500">We’ll shape the commercials.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-base sm:text-lg leading-relaxed">
            Every brand is at a different stage, so we don’t do off-the-rack
            packages. Tell us where you’re headed and we’ll build the
            engagement around it.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {TIERS.map((t, idx) => {
            const Icon = t.icon;
            return (
              <motion.div
                key={t.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: idx * 0.07 }}
                className={`relative rounded-2xl p-[1px] ${
                  t.highlight
                    ? "bg-gradient-to-br from-yellow-400 via-orange-400 to-orange-600"
                    : "bg-white/10"
                } ${t.highlight ? "lg:-translate-y-2" : ""}`}
                data-testid={`pricing-tier-${t.key}`}
              >
                <div className="relative rounded-2xl bg-zinc-950 h-full p-7 flex flex-col">
                  {t.highlight && (
                    <div className="absolute -top-3 left-7 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400 text-zinc-950 text-xs font-bold">
                      <Sparkles size={12} /> Most Popular
                    </div>
                  )}

                  <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-yellow-400/10 border border-yellow-400/25 text-yellow-300 mb-5">
                    <Icon size={20} />
                  </div>

                  <h3 className="font-display text-2xl font-semibold tracking-tight">
                    {t.name}
                  </h3>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
                    {t.blurb}
                  </p>

                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-4xl font-bold tracking-tighter brand-gradient-text">
                      Custom
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 font-mono-ui mt-1">
                    Tailored to your stage
                  </div>

                  <ul className="mt-6 space-y-2.5 flex-1">
                    {t.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2.5 text-zinc-300 text-sm leading-relaxed"
                      >
                        <span
                          className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                            t.highlight
                              ? "bg-yellow-400 text-zinc-950"
                              : "bg-white/10 text-yellow-300"
                          }`}
                        >
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#contact"
                    data-testid={`pricing-cta-${t.key}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-bold transition-all hover:-translate-y-0.5 ${
                      t.highlight
                        ? "bg-yellow-400 text-zinc-950 hover:bg-yellow-300"
                        : "border border-white/15 text-zinc-100 hover:border-yellow-400/50 hover:text-yellow-300"
                    }`}
                  >
                    Get a quote
                    <ArrowRight size={16} />
                  </a>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
